import MojitoCore from 'mojito/core';
import { noop } from 'mojito/utils';
import BetsUtils from 'services/bets/utils';
import TaskRunner from 'services/common/task-runner/task-runner.js';
import RefreshSettledBetsTask from 'services/bets/task/refresh-settled-bets-task';
import { actions } from './settled-bets-slice.js';

const { dispatch } = MojitoCore.Services.redux.store;

const NULL_SERVICE = {
    configure: noop,
    refreshBets: BetsUtils.voidResponder('betsService'),
};

const SECOND = 1000;
const MINUTE = 60 * SECOND;

/**
 * Do not request SettledBets more than every ten seconds.
 *
 * @typedef SETTLED_BETS_REQUESTS_MIN_INTERVAL_MS
 * @type {number}
 * @memberof Mojito.Services.Bets.settledBetsDataRetriever
 */
export const SETTLED_BETS_REQUESTS_MIN_INTERVAL_MS = 10 * SECOND;

const DEFAULT_POLL_SETTLED_BETS_INTERVAL_MS = 3 * MINUTE;
const DEFAULT_GET_SETTLED_BETS_LAST_HOURS = 72;
const DEFAULT_GET_SETTLED_BETS_AMOUNT = 20;

/**
 * Settled bets data retriever config.
 *
 * @typedef SettledBetsDataRetrieverConfig
 * @type {object}
 * @property {Mojito.Services.Bets.AbstractBetsService} service - Instance of concrete bets service implementation.
 * @property {string} serviceUrl - URL that will be used by <code>service<code/> instance.
 * @property {Mojito.Services.Bets.settledBetsDataRetriever.SettledBetsDataRetrieverOptionsConfig} settledBetsConfig - Settled bets setting config.
 *
 * @memberof Mojito.Services.Bets.settledBetsDataRetriever
 */

/**
 * Config that holds options to retrieve settled bets.
 *
 * @typedef SettledBetsDataRetrieverOptionsConfig
 * @property {number} [pollIntervalSec = 180] - Interval in seconds to poll settled bets. Polling will be disabled if set to 0.
 * @property {number} [timePeriodInHours = 72] - Max age of settled bets.
 * @memberof Mojito.Services.Bets.settledBetsDataRetriever
 */

/**
 * Settled bets data retriever used to delegate calls to bets service abstraction.
 *
 * @class SettledBetsDataRetriever
 * @name settledBetsDataRetriever
 * @memberof Mojito.Services.Bets
 */
class SettledBetsDataRetriever {
    constructor() {
        this.service = NULL_SERVICE;
    }

    /**
     * Init bets data retriever.
     *
     * @param {Mojito.Services.Bets.settledBetsDataRetriever.SettledBetsDataRetrieverConfig} config - Config object.
     *
     * @function Mojito.Services.Bets.settledBetsDataRetriever#init
     */
    init({ service, serviceUrl, settledBetsConfig = {} }) {
        this.service = service || NULL_SERVICE;
        this.service.configure({ serviceUrl });

        const { pollIntervalSec, timePeriodInHours, betsAmount } = settledBetsConfig;
        this.getSettledBetsLastHours = timePeriodInHours || DEFAULT_GET_SETTLED_BETS_LAST_HOURS;
        this.getSettledBetsAmount = betsAmount || DEFAULT_GET_SETTLED_BETS_AMOUNT;
        this.settledBetsPollingDisabled = pollIntervalSec === 0;
        this.setUpPollingTasks(settledBetsConfig);
    }

    /**
     * Check if settled bets polling is allowed (not disabled on config level).
     *
     * @returns {boolean} True is settled bets polling is allowed.
     */
    allowSettledBetsPolling() {
        return !this.settledBetsPollingDisabled;
    }

    /**
     * Set up settled bets polling.
     *
     * @param {Mojito.Services.Bets.settledBetsDataRetriever.SettledBetsConfig} config - Settled bets config.
     * @private
     */
    setUpPollingTasks(config) {
        const { pollIntervalSec } = config;

        if (this.allowSettledBetsPolling()) {
            this.pollSettledBetsInterval = isNaN(pollIntervalSec)
                ? DEFAULT_POLL_SETTLED_BETS_INTERVAL_MS
                : pollIntervalSec * SECOND;
            this.refreshSettledBetsRunner = new TaskRunner(this.pollSettledBetsInterval);
        }

        this.refreshSettledBetsTask = new RefreshSettledBetsTask(
            this.service,
            this.getSettledBetsLastHours,
            this.getSettledBetsAmount
        );
    }

    /**
     * Fetch settled bets without using polling.
     *
     * @param {Mojito.Services.UserSettings.types.ODDS_FORMAT} [oddsFormat] - Odds format.
     * @function Mojito.Services.Bets.settledBetsDataRetriever#fetchSettledBets
     */
    fetchSettledBets(oddsFormat) {
        const filterCriteria = this.getSettledBetsFilterCriteria();
        const recentAmount = this.getSettledBetsAmount;
        this.service
            .getBets({ settled: true, recentAmount, filterCriteria, oddsFormat })
            .then(data => dispatch(actions.fetchSettledBetsSuccess(data)))
            .catch(error => dispatch(actions.fetchSettledBetsFailed(error)));
    }

    /**
     * Get filter criteria for getting settled bets.
     *
     * @returns {Mojito.Services.Bets.types.FilterCriteria} Filter criteria.
     * @private
     */
    getSettledBetsFilterCriteria() {
        const dateSince = new Date();
        dateSince.setHours(dateSince.getHours() - this.getSettledBetsLastHours);
        return {
            from: dateSince.toISOString(),
        };
    }

    /**
     * Start refreshing settled bets.
     *
     * @param {Function} [dataHashRetriever] - Function that returns data integrity hash, typically arrives from server and used for client/backend bets cache validation.
     * @param {Function} [onRequest] - Callback that will be run when we start fetching the bets.
     *
     * @function Mojito.Services.Bets.settledBetsDataRetriever#refreshSettledBets
     */
    refreshSettledBets(dataHashRetriever, onRequest) {
        if (this.canExecute(this.refreshSettledBetsRunner)) {
            this.refreshSettledBetsTask.setDataHashRetriever(dataHashRetriever);
            this.refreshSettledBetsRunner.run(this.refreshSettledBetsTask, onRequest);
        }
    }

    /**
     * Check if we can execute the task.
     *
     * @param {Mojito.Services.Common.types.TaskRunner} taskRunner - Task runner instance.
     *
     * @returns {boolean} True is task can be executed.
     */
    canExecute(taskRunner) {
        if (this.settledBetsPollingDisabled || !taskRunner || taskRunner.isExecuting) {
            return false;
        }

        const currentTime = new Date().getTime();
        const lastExecutedTime = taskRunner.lastExecutedTime;
        return lastExecutedTime + SETTLED_BETS_REQUESTS_MIN_INTERVAL_MS < currentTime;
    }

    /**
     * Stop refreshing settled bets.
     *
     * @function Mojito.Services.Bets.settledBetsDataRetriever#stopRefreshingSettledBets
     */
    stopRefreshingSettledBets() {
        if (this.refreshSettledBetsRunner) {
            this.refreshSettledBetsRunner.reset();
        }
    }
}

export default new SettledBetsDataRetriever();
